import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtCurrency, fmtDate, fmtMonth, fmtNum } from "@/lib/format";

// Subtle blue/teal ramp consistent with the dashboard charts.
const SERIES_COLORS = [
  "hsl(200 78% 44%)", "hsl(174 62% 40%)", "hsl(217 70% 56%)", "hsl(190 70% 40%)",
  "hsl(226 62% 62%)", "hsl(166 55% 46%)", "hsl(206 62% 66%)", "hsl(240 42% 60%)",
];
const seriesColor = (i) =>
  i < SERIES_COLORS.length
    ? SERIES_COLORS[i]
    : `hsl(${185 + ((i * 17) % 70)} ${55 + ((i * 7) % 20)}% ${38 + ((i * 11) % 30)}%)`;
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Legend,
} from "recharts";
import { toast } from "sonner";
import {
  ArrowLeft, Play, Save, Trash2, Plus, X, FileSpreadsheet, FileDown,
  Table2, BarChart3, LineChart as LineIcon, AreaChart as AreaIcon, PieChart as PieIcon, Loader2,
} from "lucide-react";

const AGG_FNS = [
  { value: "sum", label: "Sum" }, { value: "avg", label: "Average" },
  { value: "min", label: "Min" }, { value: "max", label: "Max" },
  { value: "count", label: "Count of rows" },
];
const OPS_BY_TYPE = {
  text: [["eq", "equals"], ["neq", "not equals"], ["contains", "contains"], ["in", "is one of (comma-sep)"]],
  dropdown: [["eq", "equals"], ["neq", "not equals"], ["in", "is one of (comma-sep)"]],
  number: [["eq", "="], ["neq", "≠"], ["gt", ">"], ["gte", ">="], ["lt", "<"], ["lte", "<="]],
  currency: [["eq", "="], ["neq", "≠"], ["gt", ">"], ["gte", ">="], ["lt", "<"], ["lte", "<="]],
  date: [["eq", "on"], ["gte", "on/after"], ["lte", "on/before"]],
  boolean: [["eq", "is"]],
};
const CHART_TYPES = [
  { value: "table", label: "Table", icon: Table2 },
  { value: "bar", label: "Bar", icon: BarChart3 },
  { value: "line", label: "Line", icon: LineIcon },
  { value: "area", label: "Area", icon: AreaIcon },
  { value: "pie", label: "Pie", icon: PieIcon },
];

const fmtCell = (v, type) => {
  if (v == null || v === "") {
    if (type === "currency") return <span className="text-slate-400">$</span>;
    if (type === "percentage") return <span className="text-slate-400">%</span>;
    return "-";
  }
  if (type === "currency") return fmtCurrency(v, 2);
  if (type === "percentage") return `${fmtNum(v, 2)}%`;
  if (type === "number") return fmtNum(v, 2);
  if (type === "boolean") return v ? "Yes" : "No";
  if (type === "date") return String(v).match(/^\d{4}-\d{2}-01$/) ? fmtMonth(v) : fmtDate(v);
  return String(v);
};
const fmtGroupTick = (v, type) =>
  type === "date" && String(v).match(/^\d{4}-\d{2}-\d{2}$/) ? fmtMonth(v) : String(v);

export default function ReportBuilderPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canSave = Boolean(user?.role);
  const [catalog, setCatalog] = useState(null); // {sources:{...}, financial_access}
  const [saved, setSaved] = useState([]);
  // --- report definition ---
  const [source, setSource] = useState("revenue_details");
  const [mode, setMode] = useState("grouped"); // grouped | raw
  const [columns, setColumns] = useState([]);
  const [filters, setFilters] = useState([]);
  const [groupBy, setGroupBy] = useState("reporting_month");
  const [metrics, setMetrics] = useState([{ field: "billable_ftes", fn: "sum" }]);
  const [chartType, setChartType] = useState("bar");
  const [limit, setLimit] = useState(500);
  // --- results / ui state ---
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [reportName, setReportName] = useState("");
  const [activeReport, setActiveReport] = useState(null); // loaded saved report
  const [deleting, setDeleting] = useState(null);
  const previewRef = useRef(null);

  const fields = useMemo(() => catalog?.sources?.[source]?.fields || [], [catalog, source]);
  const fieldByKey = useMemo(() => Object.fromEntries(fields.map((f) => [f.key, f])), [fields]);
  const usable = (f) => catalog?.financial_access || !f.financial;
  const numericFields = fields.filter((f) => ["number", "currency"].includes(f.type) && usable(f));
  const groupableFields = fields.filter((f) => f.type !== "number" && f.type !== "currency" && usable(f));

  const loadSaved = () => api.get("/reports").then((r) => setSaved(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/reports/fields")
      .then((r) => setCatalog(r.data))
      .catch(() => toast.error("Failed to load report fields"));
    loadSaved();
  }, []);

  // reset definition when the source changes
  const onSourceChange = (s) => {
    setSource(s);
    setColumns([]);
    setFilters([]);
    setMetrics([{ fn: "count", field: "" }]);
    setGroupBy("");
    setResult(null);
    setActiveReport(null);
  };

  const buildConfig = () => ({
    source,
    columns: mode === "raw" ? columns : [],
    filters: filters.filter((f) => f.field && (f.value !== "" || f.op === "eq")).map((f) => ({
      field: f.field, op: f.op, value: f.value,
    })),
    group_by: mode === "grouped" ? groupBy || null : null,
    metrics: mode === "grouped" ? metrics.filter((m) => m.fn === "count" || m.field) : [],
    sort_by: mode === "grouped" ? "group" : undefined,
    sort_dir: "asc",
    limit: Math.max(1, Math.min(parseInt(limit) || 500, 2000)),
  });

  const run = async () => {
    if (mode === "grouped" && !groupBy) { toast.error("Pick a 'Group by' field first."); return; }
    if (mode === "grouped" && !metrics.some((m) => m.fn === "count" || m.field)) {
      toast.error("Add at least one metric."); return;
    }
    if (mode === "raw" && columns.length === 0) { toast.error("Select at least one column."); return; }
    setRunning(true);
    try {
      const res = await api.post("/reports/run", buildConfig());
      setResult(res.data);
      if (res.data.row_count === 0) toast.info("No rows matched your filters.");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Report failed"));
    } finally {
      setRunning(false);
    }
  };

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const name = activeReport?.name || "Custom Report";
      const res = await api.post(`/reports/export?name=${encodeURIComponent(name)}`,
        buildConfig(), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name.replace(/[^\w -]/g, "_")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel report downloaded");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Excel download failed"));
    } finally {
      setDownloading(false);
    }
  };

  const downloadPdf = async () => {
    if (!previewRef.current) return;
    setDownloading(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import("html2canvas"), import("jspdf"),
      ]);
      const canvas = await html2canvas(previewRef.current, { scale: 2, backgroundColor: "#ffffff" });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const pw = pdf.internal.pageSize.getWidth() - 48;
      const ph = pdf.internal.pageSize.getHeight() - 72;
      const ratio = Math.min(pw / canvas.width, ph / canvas.height);
      const name = activeReport?.name || "Custom Report";
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(14);
      pdf.text(name, 24, 30);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(120);
      pdf.text(`Global CRT (Client Revenue Tracker) · generated ${new Date().toLocaleString()}`, 24, 44);
      pdf.addImage(img, "PNG", 24, 56, canvas.width * ratio, canvas.height * ratio);
      pdf.save(`${name.replace(/[^\w -]/g, "_")}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF generation failed");
    } finally {
      setDownloading(false);
    }
  };

  const saveReport = async () => {
    const name = reportName.trim();
    if (!name) { toast.error("Give the report a name."); return; }
    try {
      const payload = { name, config: buildConfig(), chart: { type: chartType, mode } };
      if (activeReport && activeReport.created_by === user?.user_id) {
        await api.put(`/reports/${activeReport.id}`, payload);
        toast.success(`Report "${name}" updated`);
      } else {
        const res = await api.post("/reports", payload);
        setActiveReport(res.data);
        toast.success(`Report "${name}" saved`);
      }
      setSaveOpen(false);
      loadSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    }
  };

  const loadReport = (rep) => {
    const c = rep.config || {};
    setActiveReport(rep);
    setSource(c.source || "revenue_details");
    setMode(c.group_by ? "grouped" : "raw");
    setColumns(c.columns || []);
    setFilters((c.filters || []).map((f) => ({ ...f })));
    setGroupBy(c.group_by || "");
    setMetrics(c.metrics?.length ? c.metrics : [{ fn: "count", field: "" }]);
    setChartType(rep.chart?.type || "table");
    setLimit(c.limit || 500);
    setReportName(rep.name);
    setResult(null);
    toast.info(`Loaded "${rep.name}" — click Run to refresh results`);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/reports/${deleting.id}`);
      toast.success("Report deleted");
      if (activeReport?.id === deleting.id) setActiveReport(null);
      setDeleting(null);
      loadSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Delete failed"));
    }
  };

  // ---------- chart data ----------
  const chartData = useMemo(() => {
    if (!result || result.mode !== "grouped") return [];
    const gtype = result.columns[0]?.type;
    return result.rows.map((r) => ({ ...r, groupLabel: fmtGroupTick(r.group, gtype) }));
  }, [result]);
  const metricCols = result?.mode === "grouped" ? result.columns.slice(1) : [];

  const renderChart = () => {
    if (chartType === "pie") {
      const key = metricCols[0]?.key;
      return (
        <ResponsiveContainer width="100%" height={420}>
          <PieChart>
            <Pie data={chartData} dataKey={key} nameKey="groupLabel" cx="50%" cy="50%"
              outerRadius={150} label={(e) => e.groupLabel}>
              {chartData.map((_, i) => <Cell key={i} fill={seriesColor(i)} />)}
            </Pie>
            <ReTooltip formatter={(v) => fmtNum(v, 2)} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    const Wrapper = chartType === "line" ? LineChart : chartType === "area" ? AreaChart : BarChart;
    return (
      <ResponsiveContainer width="100%" height={420}>
        <Wrapper data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="groupLabel" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" interval={0} height={70} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtNum(v, 0)} />
          <ReTooltip formatter={(v) => fmtNum(v, 2)} />
          {metricCols.length > 1 && <Legend />}
          {metricCols.map((m, i) => {
            const color = seriesColor(i);
            if (chartType === "line") {
              return <Line key={`${m.key}_${i}`} type="monotone" dataKey={m.key} name={m.label} stroke={color} strokeWidth={2} dot={false} />;
            }
            if (chartType === "area") {
              return <Area key={`${m.key}_${i}`} type="monotone" dataKey={m.key} name={m.label} stroke={color} fill={color} fillOpacity={0.25} />;
            }
            return <Bar key={`${m.key}_${i}`} dataKey={m.key} name={m.label} fill={color} radius={[4, 4, 0, 0]} />;
          })}
        </Wrapper>
      </ResponsiveContainer>
    );
  };

  if (!catalog) return <Skeleton className="h-[500px] rounded-xl" data-testid="report-builder-loading" />;

  return (
    <div className="space-y-4" data-testid="report-builder-page">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" className="rounded-lg" onClick={() => navigate("/dashboard")}
          data-testid="report-builder-back-button">
          <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
        </Button>
        <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Space Grotesk" }}>
          Report Builder
        </h2>
        {activeReport && <Badge variant="outline" className="rounded-full">{activeReport.name}</Badge>}
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" className="rounded-lg" onClick={run} disabled={running} data-testid="report-run-button">
            {running ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />} Run
          </Button>
          {canSave && (
            <Button size="sm" variant="outline" className="rounded-lg" data-testid="report-save-button"
              onClick={() => { setReportName(activeReport?.name || ""); setSaveOpen(true); }}>
              <Save className="h-4 w-4 mr-1" /> Save
            </Button>
          )}
          <Button size="sm" variant="outline" className="rounded-lg" onClick={downloadExcel}
            disabled={downloading || !result} data-testid="report-excel-button">
            <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
          </Button>
          <Button size="sm" variant="outline" className="rounded-lg" onClick={downloadPdf}
            disabled={downloading || !result} data-testid="report-pdf-button">
            <FileDown className="h-4 w-4 mr-1" /> PDF
          </Button>
        </div>
      </div>

      {/* Saved reports strip */}
      {saved.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="saved-reports-strip">
          <span className="text-xs text-slate-500">Saved reports:</span>
          {saved.map((rep) => (
            <span key={rep.id} className="inline-flex items-center rounded-full border bg-white text-xs shadow-sm overflow-hidden">
              <button className={`px-3 py-1.5 hover:bg-teal-50 transition-[background-color] duration-150 ${activeReport?.id === rep.id ? "bg-teal-50 text-teal-700 font-medium" : "text-slate-600"}`}
                onClick={() => loadReport(rep)} data-testid={`saved-report-${rep.id}`}>
                {rep.name}
              </button>
              {(canSave && (rep.created_by === user?.user_id || user?.role === "cxo" || user?.role === "super_admin")) && (
                <button className="px-1.5 py-1.5 text-slate-300 hover:text-red-500 border-l"
                  onClick={() => setDeleting(rep)} data-testid={`saved-report-delete-${rep.id}`}
                  title="Delete report">
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 items-start">
        {/* ---------------- Config panel ---------------- */}
        <Card className="rounded-xl border bg-card shadow-[var(--app-shadow-sm)] p-4 space-y-4" data-testid="report-config-panel">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-400">Data source</Label>
            <Select value={source} onValueChange={onSourceChange}>
              <SelectTrigger data-testid="report-source-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(catalog.sources).map(([k, s]) => (
                  <SelectItem key={k} value={k}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-400">Report type</Label>
            <div className="grid grid-cols-2 gap-1.5">
              <Button variant={mode === "grouped" ? "default" : "outline"} size="sm" className="rounded-lg"
                onClick={() => { setMode("grouped"); setResult(null); }} data-testid="report-mode-grouped">
                Summary / Chart
              </Button>
              <Button variant={mode === "raw" ? "default" : "outline"} size="sm" className="rounded-lg"
                onClick={() => { setMode("raw"); setChartType("table"); setResult(null); }} data-testid="report-mode-raw">
                Detail rows
              </Button>
            </div>
          </div>

          {mode === "grouped" ? (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-400">Group by</Label>
                <Select value={groupBy} onValueChange={setGroupBy}>
                  <SelectTrigger data-testid="report-groupby-select"><SelectValue placeholder="Pick a field" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {groupableFields.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wide text-slate-400">Metrics</Label>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" data-testid="report-add-metric"
                    onClick={() => setMetrics([...metrics, { fn: "sum", field: numericFields[0]?.key || "" }])}>
                    <Plus className="h-3 w-3 mr-1" /> Add
                  </Button>
                </div>
                {metrics.map((m, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <Select value={m.fn} onValueChange={(v) => setMetrics(metrics.map((x, j) => j === i ? { ...x, fn: v } : x))}>
                      <SelectTrigger className="w-[110px] h-8 text-xs" data-testid={`report-metric-fn-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{AGG_FNS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                    </Select>
                    {m.fn !== "count" && (
                      <Select value={m.field} onValueChange={(v) => setMetrics(metrics.map((x, j) => j === i ? { ...x, field: v } : x))}>
                        <SelectTrigger className="flex-1 h-8 text-xs" data-testid={`report-metric-field-${i}`}><SelectValue placeholder="Field" /></SelectTrigger>
                        <SelectContent>{numericFields.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                    {metrics.length > 1 && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500"
                        onClick={() => setMetrics(metrics.filter((_, j) => j !== i))} data-testid={`report-remove-metric-${i}`}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-400">Visualization</Label>
                <div className="grid grid-cols-5 gap-1">
                  {CHART_TYPES.map((c) => (
                    <button key={c.value} onClick={() => setChartType(c.value)} title={c.label}
                      data-testid={`report-chart-${c.value}`}
                      className={`flex flex-col items-center gap-0.5 rounded-lg border px-1 py-2 text-[10px] transition-[background-color,border-color] duration-150 ${chartType === c.value ? "border-teal-500 bg-teal-50 text-teal-700" : "text-slate-500 hover:bg-slate-50"}`}>
                      <c.icon className="h-4 w-4" />{c.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-400">Columns</Label>
              <div className="max-h-56 overflow-y-auto space-y-0.5 rounded-lg border p-2">
                {fields.map((f) => (
                  <label key={f.key} className="flex items-center gap-2 rounded px-1.5 py-1 hover:bg-slate-50 cursor-pointer">
                    <Checkbox checked={columns.includes(f.key)} data-testid={`report-column-${f.key}`}
                      onCheckedChange={(c) => setColumns(c ? [...columns, f.key] : columns.filter((k) => k !== f.key))} />
                    <span className="text-xs truncate">{f.label}</span>
                    {f.financial && !catalog.financial_access && (
                      <span className="ml-auto text-[10px] text-slate-400">$ masked</span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wide text-slate-400">Filters</Label>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" data-testid="report-add-filter"
                onClick={() => setFilters([...filters, { field: groupableFields[0]?.key || fields[0]?.key, op: "eq", value: "" }])}>
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>
            {filters.length === 0 && <p className="text-[11px] text-slate-400">No filters — all rows included.</p>}
            {filters.map((f, i) => {
              const meta = fieldByKey[f.field] || {};
              const ftype = meta.key === "reporting_month" ? "date" : meta.type;
              const ops = OPS_BY_TYPE[meta.type] || OPS_BY_TYPE.text;
              return (
                <div key={i} className="space-y-1 rounded-lg border p-2">
                  <div className="flex items-center gap-1.5">
                    <Select value={f.field} onValueChange={(v) => setFilters(filters.map((x, j) => j === i ? { ...x, field: v, op: "eq", value: "" } : x))}>
                      <SelectTrigger className="flex-1 h-8 text-xs" data-testid={`report-filter-field-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        {fields.filter(usable).map((fd) => <SelectItem key={fd.key} value={fd.key}>{fd.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500"
                      onClick={() => setFilters(filters.filter((_, j) => j !== i))} data-testid={`report-remove-filter-${i}`}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Select value={f.op} onValueChange={(v) => setFilters(filters.map((x, j) => j === i ? { ...x, op: v } : x))}>
                      <SelectTrigger className="w-[120px] h-8 text-xs" data-testid={`report-filter-op-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{ops.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                    {meta.type === "dropdown" && f.op !== "in" ? (
                      <Select value={String(f.value)} onValueChange={(v) => setFilters(filters.map((x, j) => j === i ? { ...x, value: v } : x))}>
                        <SelectTrigger className="flex-1 h-8 text-xs" data-testid={`report-filter-value-${i}`}><SelectValue placeholder="Value" /></SelectTrigger>
                        <SelectContent>{(meta.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : meta.type === "boolean" ? (
                      <Select value={String(f.value)} onValueChange={(v) => setFilters(filters.map((x, j) => j === i ? { ...x, value: v === "true" } : x))}>
                        <SelectTrigger className="flex-1 h-8 text-xs" data-testid={`report-filter-value-${i}`}><SelectValue placeholder="Value" /></SelectTrigger>
                        <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                      </Select>
                    ) : (
                      <Input className="flex-1 h-8 text-xs" data-testid={`report-filter-value-${i}`}
                        type={["number", "currency"].includes(ftype) ? "number" : meta.key === "reporting_month" ? "month" : ftype === "date" ? "date" : "text"}
                        placeholder={f.op === "in" ? "A, B, C" : "Value"}
                        value={f.op === "in" && Array.isArray(f.value) ? f.value.join(",") : f.value ?? ""}
                        onChange={(e) => setFilters(filters.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-400">Row limit</Label>
            <Input type="number" min="1" max="2000" className="h-8 text-xs w-28" value={limit}
              onChange={(e) => setLimit(e.target.value)} data-testid="report-limit-input" />
          </div>
        </Card>

        {/* ---------------- Preview ---------------- */}
        <Card className="rounded-xl border bg-card shadow-[var(--app-shadow-sm)] p-4 min-h-[480px]" data-testid="report-preview-panel">
          {!result ? (
            <div className="h-[440px] flex flex-col items-center justify-center text-center gap-3 text-slate-400">
              <BarChart3 className="h-10 w-10" />
              <div>
                <p className="text-sm font-medium text-slate-500">Build your report, then click Run</p>
                <p className="text-xs mt-1">Pick a data source, group it, add metrics and filters —<br />then download as Excel or PDF.</p>
              </div>
            </div>
          ) : (
            <div ref={previewRef} className="bg-white" data-testid="report-preview-content">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-slate-800" style={{ fontFamily: "Space Grotesk" }}>
                  {activeReport?.name || "Custom Report"}
                  <span className="ml-2 text-xs font-normal text-slate-400">{result.row_count} row{result.row_count === 1 ? "" : "s"}</span>
                </p>
              </div>
              {result.mode === "grouped" && chartType !== "table" && chartData.length > 0 ? (
                renderChart()
              ) : (
                <div className="overflow-auto max-h-[520px] rounded-lg border">
                  <table className="w-full text-xs" data-testid="report-result-table">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr className="border-b">
                        {result.columns.map((c, ci) => (
                          <th key={`${c.key}_${ci}`} className={`px-3 py-2 font-semibold text-slate-600 whitespace-nowrap ${["number", "currency", "percentage"].includes(c.type) ? "text-right" : "text-left"}`}>
                            {c.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.rows.map((r, i) => (
                        <tr key={i} className="border-b last:border-0 hover:bg-slate-50">
                          {result.columns.map((c, ci) => (
                            <td key={`${c.key}_${ci}`} className={`px-3 py-1.5 whitespace-nowrap ${["number", "currency", "percentage"].includes(c.type) ? "text-right tabular-nums" : ""}`}>
                              {c.key === "group" ? fmtGroupTick(r.group, c.type) : fmtCell(r[c.key], c.type)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* Save dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Save report</DialogTitle>
            <DialogDescription>
              Saved reports are visible to everyone in this workspace.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Report name</Label>
            <Input value={reportName} onChange={(e) => setReportName(e.target.value)}
              placeholder="e.g. Monthly FTE by State" data-testid="report-name-input"
              onKeyDown={(e) => e.key === "Enter" && saveReport()} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button onClick={saveReport} data-testid="report-save-confirm">
              {activeReport && activeReport.created_by === user?.user_id ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete report?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting && `"${deleting.name}" will be removed for everyone in this workspace. This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700"
              data-testid="report-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
